import isISO8601 from 'validator/lib/isISO8601';
import isInt from 'validator/lib/isInt';
import isFloat from 'validator/lib/isFloat';
import isEmail from 'validator/lib/isEmail';
import { incaAccessor } from '@cancercentrum/inca';
import { formatISO, isAfter, isBefore, isSameDay, isValid, parse, parseISO } from 'date-fns';
const reDate = /^\d{4}-\d{2}-\d{2}$/;
const reDateWithTime = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
const isISO8601Date = (input)=>isISO8601((input || '').toString(), {
        strict: true
    });
export const registerDefaultMethods = (val)=>{
    val.addMethod('required', (value)=>{
        return value !== undefined && value !== null && value !== '' && value !== false;
    }, 'validation.required');
    val.addMethod('ISO8601', function(value) {
        return this.optional(value) || isISO8601Date(value);
    }, 'validation.ISO8601');
    val.addMethod('isoDate', function(value) {
        return this.optional(value) || reDate.test(value || '') && isValid(parse(value || '', 'yyyy-MM-dd', new Date()));
    }, 'validation.isoDate');
    val.addMethod('isoDateWithTime', function(value) {
        return this.optional(value) || reDateWithTime.test(value || '') && isValid(parse(value || '', 'yyyy-MM-dd HH:mm:ss', new Date()));
    }, 'validation.isoDateWithTime');
    val.addMethod('maxDate', function(value, param) {
        const dateValue = parseISO(value || '');
        const dateMax = parseISO(param || '');
        return this.optional(value) || !isValid(dateValue) || !isValid(dateMax) || !isBefore(dateMax, dateValue);
    }, 'validation.maxDate');
    val.addMethod('beforeToday', function(value) {
        const dateValue = parseISO(value || '');
        return this.optional(value) || !isValid(dateValue) || !isAfter(dateValue, new Date());
    }, 'validation.beforeToday');
    val.addMethod('minDate', function(value, param) {
        const dateMin = parseISO(param || '');
        const dateValue = parseISO(value || '');
        return this.optional(value) || !isValid(dateMin) || !isValid(dateValue) || !isAfter(dateMin, dateValue);
    }, 'validation.minDate');
    val.addMethod('year', function(value) {
        const yearRegex = /^[1-9]\d{3}$/;
        return this.optional(value) || yearRegex.test(value);
    }, 'validation.year');
    val.addMethod('integer', function(value) {
        return this.optional(value) || isInt(value.toString());
    }, 'validation.integer');
    val.addMethod('pattern', function(value, param) {
        return this.optional(value) || param.test(value);
    }, 'validation.pattern');
    val.addMethod('float', function(value) {
        return this.optional(value) || isFloat(value.toString());
    }, 'validation.float');
    val.addMethod('boolean', function(value) {
        return this.optional(value) || typeof value === 'boolean';
    }, 'validation.boolean');
    val.addMethod('number', function(value) {
        return this.optional(value) || !isNaN(+value);
    }, 'validation.number');
    val.addMethod('min', function(value, param) {
        return this.optional(value) || value >= param;
    }, 'validation.min');
    val.addMethod('max', function(value, param) {
        return this.optional(value) || value <= param;
    }, 'validation.max');
    val.addMethod('digits', function(value) {
        return this.optional(value) || /^\d+$/.test(value);
    }, 'validation.digits');
    val.addMethod('range', function(value, param) {
        return this.optional(value) || value >= param.min && value <= param.max;
    }, (params)=>{
        return {
            phrase: 'validation.range',
            params
        };
    });
    val.addMethod('email', function(value) {
        return this.optional(value) || isEmail(value);
    }, 'validation.email');
    val.addMethod('minLength', function(value, param) {
        return this.optional(value) || typeof value === 'string' && value.length >= param;
    }, 'validation.minLength');
    val.addMethod('maxLength', function(value, param) {
        return this.optional(value) || typeof value === 'string' && value.length <= param;
    }, 'validation.maxLength');
    val.addMethod('rangeLength', function(value, param) {
        return this.optional(value) || typeof value === 'string' && value.length >= param.min && value.length <= param.max;
    }, (params)=>{
        return {
            phrase: 'validation.rangeLength',
            params
        };
    });
    val.addMethod('validCharacters', function(value, param) {
        return this.optional(value) || typeof value === 'string' && value.split('').every((c)=>param.indexOf(c) !== -1);
    }, 'validation.validCharacters');
    val.addMethod('invalid', function(value) {
        return this.optional(value) || !value;
    }, 'validation.invalid');
};
export const registerDesignedFormMethods = (val, patient)=>{
    val.addMethod('birthDate', function(value) {
        const dateValue = parseISO(value || '');
        const birthDate = parseISO((patient ? patient.dateOfBirth : incaAccessor().form.env._FODELSEDATUM) || '');
        return this.optional(value) || !isValid(dateValue) || !isValid(birthDate) || isSameDay(dateValue, birthDate) || isAfter(dateValue, birthDate);
    }, ()=>{
        const birthDateStr = (patient ? patient.dateOfBirth : incaAccessor().form.env._FODELSEDATUM) || '';
        const birthDate = parseISO(birthDateStr);
        return {
            phrase: 'validation.birthDate',
            params: {
                date: isValid(birthDate) ? formatISO(birthDate, {
                    representation: 'date'
                }) : birthDateStr
            }
        };
    });
    val.addMethod('deathDate', function(value) {
        const dateValue = parseISO(value || '');
        const deathDate = parseISO((patient ? patient.dateOfDeath : incaAccessor().form.env._DATEOFDEATH) || '');
        return this.optional(value) || !isValid(dateValue) || !isValid(deathDate) || isSameDay(dateValue, deathDate) || isBefore(dateValue, deathDate);
    }, ()=>{
        const deathDateStr = (patient ? patient.dateOfDeath : incaAccessor().form.env._DATEOFDEATH) || '';
        const deathDate = parseISO(deathDateStr);
        return {
            phrase: 'validation.deathDate',
            params: {
                date: isValid(deathDate) ? formatISO(deathDate, {
                    representation: 'date'
                }) : deathDateStr
            }
        };
    });
};
